// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import type { RxDatabase } from 'rxdb';
import { QueryOperator } from '../operators/QueryOperator';
import { MutationOperator } from '../operators/MutationOperator';
import type { QueryOptions } from '../operators/types';
import type { OrgModel } from '../../models/Org';
import { createId } from '../../index';

export interface OrgFilters {
  /** Filter by user ID (owner) */
  userId?: string;
  /** Filter by archived state (default: false) */
  archived?: boolean;
}

export interface CreateOrgInput {
  /** Organization name */
  name: string;
  /** Organization description */
  description: string;
  /** User ID of organization creator/owner */
  userId: string;
}

export interface UpdateOrgInput {
  /** Org ID to update */
  id: string;
  /** Partial org data to update */
  updates: Partial<OrgModel>;
}

export class OrgOperator {
  constructor(private db: RxDatabase) {}

  query(
    filters: OrgFilters,
    options?: { sort?: string; limit?: number; skip?: number }
  ): QueryOperator<OrgModel> {
    const collection = this.db.collections.orgs;

    // Use $ne: true instead of $eq: false to match documents where archived is false, undefined, or missing
    const selector: any = {
      archived: filters.archived === true ? { $eq: true } : { $ne: true },
    };

    if (filters.userId) {
      selector.userId = { $eq: filters.userId };
    }

    const queryOptions: QueryOptions<OrgModel> = {
      selector,
      sort: options?.sort ? [{ [options.sort]: 'desc' }] : [{ createdAt: 'desc' }],
      limit: options?.limit,
      skip: options?.skip,
    };

    return new QueryOperator<OrgModel>(collection, queryOptions);
  }

  async findOne(orgId: string): Promise<OrgModel | null> {
    const doc = await this.db.collections.orgs.findOne(orgId).exec();
    return doc ? (doc.toJSON() as OrgModel) : null;
  }

  async count(filters: OrgFilters): Promise<number> {
    const collection = this.db.collections.orgs;
    // Use $ne: true instead of $eq: false to match documents where archived is false, undefined, or missing
    const selector: any = {
      archived: filters.archived === true ? { $eq: true } : { $ne: true },
    };

    if (filters.userId) {
      selector.userId = { $eq: filters.userId };
    }

    return await collection.count({ selector }).exec();
  }

  createMutation(): MutationOperator<OrgModel, CreateOrgInput> {
    return new MutationOperator<OrgModel, CreateOrgInput>(this.db.collections.orgs, {
      mutationFn: async (collection, input) => {
        const now = new Date().toISOString();
        const id = createId('org');
        const org: OrgModel = {
          id,
          userId: input.userId,
          name: input.name,
          description: input.description,
          archived: false,
          createdAt: now,
          updatedAt: now,
        };

        await collection.insert(org);
        return org;
      },
    });
  }

  updateMutation(): MutationOperator<OrgModel, UpdateOrgInput> {
    return new MutationOperator<OrgModel, UpdateOrgInput>(this.db.collections.orgs, {
      mutationFn: async (collection, { id, updates }) => {
        const doc = await collection.findOne(id).exec();
        if (!doc) throw new Error(`Org ${id} not found`);

        await doc.update({
          $set: { ...updates, updatedAt: new Date().toISOString() },
        });

        return doc.toJSON() as OrgModel;
      },
    });
  }

  deleteMutation(): MutationOperator<void, string> {
    return new MutationOperator<void, string>(this.db.collections.orgs, {
      mutationFn: async (collection, orgId) => {
        const doc = await collection.findOne(orgId).exec();
        if (!doc) throw new Error(`Org ${orgId} not found`);

        await doc.update({
          $set: { archived: true, updatedAt: new Date().toISOString() },
        });
      },
    });
  }
}
